<template>
  <div class="retain-page" v-loading="loading">
    <el-form size="small" inline>
      <el-form-item label="统计周期">
        <el-radio-group v-model="grain" @change="query">
          <el-radio-button label="month">按月</el-radio-button>
          <el-radio-button label="quarter">按季</el-radio-button>
          <el-radio-button label="half">按半年</el-radio-button>
          <el-radio-button label="year">按年</el-radio-button>
        </el-radio-group>
      </el-form-item>
      <el-form-item label="路线">
        <el-select v-model="pathId" style="width: 180px" @change="query">
          <el-option v-for="item in pathOpts" :key="item.id" :label="item.name" :value="item.id" />
        </el-select>
      </el-form-item>
      <el-form-item>
        <ai-insight ref="insight" />
      </el-form-item>
    </el-form>

    <p class="tip">只统计成交订单。新客是第一笔成交落在该周期的乘客。按月看最近 12 个月，按季看最近 8 个季度，按半年看最近 3 年，按年看最近 4
      年。最后一期截至今天，环比和同比都按同样的天数对比。回访要等该周期结束后满 30、90、180 天才有数。</p>

    <el-row :gutter="12" class="cards">
      <el-col :span="5">
        <el-card shadow="never">
          <div class="num">{{ headline.passengerCount || 0 }}</div>
          <div class="label">{{ headline.period || "本期" }} 成交人数</div>
          <div class="change">环比 <span :class="changeClass(mom.passengerCount)">{{ formatChange(mom.passengerCount)
              }}</span> 同比 <span :class="changeClass(yoy.passengerCount)">{{ formatChange(yoy.passengerCount) }}</span>
          </div>
        </el-card>
      </el-col>
      <el-col :span="5">
        <el-card shadow="never">
          <div class="num">{{ headline.ticketCount || 0 }}</div>
          <div class="label">成交票量</div>
          <div class="change">环比 <span :class="changeClass(mom.ticketCount)">{{ formatChange(mom.ticketCount) }}</span>
            同比 <span :class="changeClass(yoy.ticketCount)">{{ formatChange(yoy.ticketCount) }}</span></div>
        </el-card>
      </el-col>
      <el-col :span="5">
        <el-card shadow="never">
          <div class="num">{{ formatMoney(headline.income) }}</div>
          <div class="label">流水</div>
          <div class="change">环比 <span :class="changeClass(mom.income)">{{ formatChange(mom.income) }}</span>　同比 <span
              :class="changeClass(yoy.income)">{{ formatChange(yoy.income) }}</span></div>
        </el-card>
      </el-col>
      <el-col :span="5">
        <el-card shadow="never">
          <div class="num">{{ formatRate(headline.repurchase90Rate) }}</div>
          <div class="label">近 90 天复购率</div>
          <div class="change">本期复购率 {{ formatRate(headline.monthRepurchaseRate) }}</div>
        </el-card>
      </el-col>
      <el-col :span="4">
        <el-card shadow="never">
          <div class="num">{{ formatRate(headline.oldTicketShare) }}</div>
          <div class="label">老客票量占比</div>
          <div class="change">老客贡献的成交票量</div>
        </el-card>
      </el-col>
    </el-row>

    <div class="block-title">新客、老客和票量</div>
    <chart ref="trendChart" :height="360" />

    <div class="block-title">首乘后再次成交</div>
    <p class="tip">首乘人数是第一笔成交落在该周期的乘客。30 天回访 = 这些人里，首乘日之后 30 天内又成交的人数 ÷ 首乘人数。90 天、180
      天同样从每个乘客自己的首乘日往后数，同一天的另一笔不算再次成交。要等该周期结束并满对应天数，整组人都走完窗口才出数，否则显示横线。不足 30 人的周期整行变灰。</p>
    <el-table :data="cohorts" stripe :row-class-name="cohortRowClass" empty-text="暂无成交">
      <el-table-column prop="period" label="首乘周期" min-width="140" />
      <el-table-column prop="cohortSize" label="首乘人数" width="120" />
      <el-table-column label="30 天回访" min-width="120">
        <template slot-scope="scope">{{ formatRate(scope.row.rate30) }}</template>
      </el-table-column>
      <el-table-column label="90 天回访" min-width="120">
        <template slot-scope="scope">{{ formatRate(scope.row.rate90) }}</template>
      </el-table-column>
      <el-table-column label="180 天回访" min-width="120">
        <template slot-scope="scope">{{ formatRate(scope.row.rate180) }}</template>
      </el-table-column>
    </el-table>

    <div class="block-title">{{ headline.period || "本期" }} 各路线票量同比</div>
    <el-table :data="routes" stripe empty-text="本期还没有成交">
      <el-table-column prop="pathName" label="路线" min-width="180" />
      <el-table-column prop="tickets" label="本期票量" width="120" sortable />
      <el-table-column prop="yoyTickets" label="去年同期票量" width="140" sortable />
      <el-table-column label="同比" width="120">
        <template slot-scope="scope"><span :class="changeClass(scope.row.yoyRate)">{{ formatChange(scope.row.yoyRate)
            }}</span></template>
      </el-table-column>
    </el-table>
  </div>
</template>

<script>
import { mapState } from "vuex";
import { getPassengerHealth } from "@/api/data";
import AiInsight from "@/components/AiInsight";

export default {
  components: { AiInsight },
  computed: {
    ...mapState({
      pathOpts: (state) => [{ name: "全部路线", id: "" }, ...(state.pathList || [])]
    }),
    mom() {
      return this.headline.mom || {};
    },
    yoy() {
      return this.headline.yoy || {};
    }
  },
  data() {
    return {
      grain: "quarter",
      pathId: "",
      loading: false,
      headline: {},
      cohorts: [],
      routes: []
    };
  },
  created() {
    this.query();
  },
  methods: {
    formatMoney(value) {
      if (value == null || value === "") {
        return "—";
      }
      const number = Math.round(Number(value)) / 100;
      const sign = number < 0 ? "-" : "";
      const abs = Math.abs(number);
      if (abs >= 10000) {
        return `${sign}${(abs / 10000).toFixed(2)}万`;
      }
      return sign + abs.toLocaleString("zh-CN", { maximumFractionDigits: 0 });
    },
    formatRate(value) {
      if (value == null || value === "") {
        return "—";
      }
      return `${Number(value).toFixed(1)}%`;
    },
    formatChange(value) {
      if (value == null || value === "") {
        return "—";
      }
      const num = Number(value);
      const sign = num > 0 ? "+" : "";
      return `${sign}${num.toFixed(1)}%`;
    },
    changeClass(value) {
      if (value == null || value === "") {
        return "";
      }
      const num = Number(value);
      if (num > 0) {
        return "up";
      }
      if (num < 0) {
        return "down";
      }
      return "";
    },
    cohortRowClass({ row }) {
      return row.small ? "cohort-small" : "";
    },
    query() {
      const param = { grain: this.grain };
      if (this.pathId) {
        param.pathId = this.pathId;
      }
      this.loading = true;
      getPassengerHealth(param).then((res) => {
        const data = res.data || {};
        this.headline = data.headline || {};
        this.cohorts = data.cohorts || [];
        this.routes = data.routes || [];
        this.renderTrend(data.months || []);
        this.$nextTick(() => {
          if (!this.$refs.insight) {
            return;
          }
          this.$refs.insight.setContext("retain", {
            grain: this.grain,
            pathId: this.pathId || "",
            headline: this.headline,
            months: data.months || [],
            cohorts: this.cohorts,
            routes: this.routes.slice(0, 8)
          });
        });
      }).finally(() => {
        this.loading = false;
      });
    },
    renderTrend(months) {
      const option = {
        tooltip: { trigger: "axis" },
        legend: { data: ["新客", "老客", "成交票量", "老客票量占比"] },
        grid: { left: 48, right: 48, bottom: 32, top: 48 },
        xAxis: { type: "category", data: months.map((item) => item.period || item.month) },
        yAxis: [
          { type: "value", name: "人数 / 票量" },
          { type: "value", name: "占比", max: 100, axisLabel: { formatter: "{value}%" } }
        ],
        series: [
          { name: "新客", type: "bar", stack: "people", data: months.map((item) => item.newPassengers) },
          { name: "老客", type: "bar", stack: "people", data: months.map((item) => item.oldPassengers) },
          { name: "成交票量", type: "line", data: months.map((item) => item.ticketCount) },
          {
            name: "老客票量占比",
            type: "line",
            yAxisIndex: 1,
            data: months.map((item) => item.oldTicketShare)
          }
        ]
      };
      this.$nextTick(() => {
        if (this.$refs.trendChart) {
          this.$refs.trendChart.setOption(option, true);
        }
      });
    }
  }
};
</script>

<style lang="less" scoped>
.tip {
  color: #909399;
  font-size: 13px;
  line-height: 22px;
  margin: 0 0 12px;
}

.cards {
  margin-bottom: 8px;
}

.num {
  font-size: 22px;
  font-weight: 600;
  line-height: 32px;
}

.label {
  color: #606266;
  font-size: 13px;
}

.change {
  color: #909399;
  font-size: 12px;
  margin-top: 6px;
}

.up {
  color: #f56c6c;
}

.down {
  color: #67c23a;
}

.block-title {
  margin: 18px 0 8px;
  font-size: 15px;
  font-weight: 600;
}

.retain-page /deep/ .cohort-small td {
  color: #c0c4cc;
}
</style>
